/**
 * Geo helpers — browser-side distance calculations.
 *
 * Coordinates rounded to ~1 km are cached in sessionStorage (cleared when the tab closes).
 */

const STORAGE_KEY = "vyrti.userCoords";
try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* storage unavailable */ }
const round = (n) => Math.round(n * 100) / 100;

export function getCachedCoords() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { lat, lng, savedAt } = JSON.parse(raw);
    if (typeof lat !== "number" || typeof lng !== "number") return null;
    // Refresh every 7 days
    if (Date.now() - savedAt > 7 * 24 * 60 * 60 * 1000) return null;
    return { lat, lng };
  } catch (e) {
    return null;
  }
}

export function setCachedCoords(lat, lng) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ lat: round(lat), lng: round(lng), savedAt: Date.now() }));
  } catch (e) { /* ignore */ }
}

/**
 * Request the browser's geolocation. Returns a promise resolving to {lat, lng}
 * or null if the user denies / browser unsupported.
 */
export function requestCoords() {
  return new Promise(resolve => {
    if (!("geolocation" in navigator)) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCachedCoords(lat, lng);
        resolve({ lat, lng });
      },
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 24 * 60 * 60 * 1000 }
    );
  });
}

/**
 * Great-circle distance using the haversine formula. Returns miles, rounded.
 * Returns null if any coordinate is missing.
 */
export function distanceMiles(a, b) {
  if (!a || !b) return null;
  if (typeof a.lat !== "number" || typeof a.lng !== "number") return null;
  if (typeof b.lat !== "number" || typeof b.lng !== "number") return null;
  const R = 3958.8; // earth radius in miles
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const aa =
    sinDLat * sinDLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinDLng * sinDLng;
  const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
  const miles = R * c;
  return Math.round(miles);
}

/**
 * Format a distance for display.
 *  - < 1   → "Nearby"
 *  - < 100 → "14 miles away"
 *  - >= 100 → "Ships nationwide"
 */
export function formatDistance(miles) {
  if (miles === null || miles === undefined) return null;
  if (miles < 1) return "Nearby";
  if (miles < 100) return `${miles} ${miles === 1 ? "mile" : "miles"} away`;
  return "Ships nationwide";
}
